/**
 * FannedStackBase — the SHARED STACKING MECHANIC behind the fanned
 * archives in the File Completed column (CLAUDE.md §20).
 *
 * Extracted verbatim from FannedStack so the same interaction engine can
 * drive more than one card type without duplicating it:
 *   - FannedStack.jsx      → completed FILE FOLDERS (navy tab presentation)
 *   - PrepFannedStack.jsx  → completed PREP REQUESTS (notebook paper strips)
 *
 * The base owns everything that is presentation-agnostic:
 *   - hover-intent timing (80 ms enter delay, 150 ms leave grace),
 *   - exclusive expansion (a single `expandedId` per stack),
 *   - hover-capability detection (mouse vs touch) and the touch protocol
 *     (first tap expands, second tap opens, tap outside collapses),
 *   - prefers-reduced-motion (instant expand/collapse, no transitions),
 *   - the stable wrapper element per item: identical DOM node across the
 *     collapsed ↔ expanded swap (so mouseenter/mouseleave never break),
 *     constant -6px overlap margin, z-index lift while expanded.
 *
 * What the collapsed and expanded states LOOK like is entirely the
 * caller's, via renderCollapsed / renderExpanded — the mechanic never
 * imposes a visual language.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

// Hover-intent timings. Kept as constants so the CLAUDE.md description
// and the runtime stay in lock-step.
const ENTER_DELAY_MS = 80;
const LEAVE_GRACE_MS = 150;

export default function FannedStackBase({
  items,
  idOf = (item) => item.id,
  // Called when the user commits to opening the item (desktop: any click;
  // touch: a tap on the already-expanded card).
  onItemOpen,
  emptyMessage = 'Nothing here yet',
  emptyStyle,
  // App-level role gate (CLAUDE.md §30). When false the current role may not
  // open these items: the hover/tap expand affordance is suppressed and a
  // click routes straight through onItemOpen (a no-op for gated roles).
  // May be a BOOLEAN (the whole stack) or a PER-ITEM PREDICATE
  // `(item) => boolean` — prep requests are openable per record (the
  // Administration Assistant may open only the ones he loaded, §26.1), so
  // the gate has to be resolved item by item rather than for the stack.
  interactive = true,
  // renderExpanded(item, { onClick, interactive }) — the full card.
  renderExpanded,
  // renderCollapsed(item, { onClick, focusBind, reducedMotion, interactive })
  // — the compact stacked strip/tab.
  renderCollapsed,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [hasHover, setHasHover] = useState(true);
  const enterTimer = useRef(null);
  const leaveTimer = useRef(null);
  const containerRef = useRef(null);
  const reducedMotion = usePrefersReducedMotion();

  // Resolve the role gate for ONE item (boolean prop → same answer for
  // every item; predicate prop → asked per item).
  const interactiveFor = useCallback(
    (item) => (typeof interactive === 'function' ? !!interactive(item) : interactive !== false),
    [interactive]
  );

  // Keep the latest `expandedId` on a ref so the timer callbacks can
  // read it without capturing a stale closure.
  const expandedIdRef = useRef(expandedId);
  useEffect(() => { expandedIdRef.current = expandedId; }, [expandedId]);

  // Detect whether the primary pointer supports hover (i.e. a mouse).
  // On hover-capable devices clicks pass straight through; on touch
  // the first tap expands and the second tap opens.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setHasHover(mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  // On touch devices, tapping outside the stack collapses the pinned item.
  useEffect(() => {
    if (hasHover || expandedId == null) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setExpandedId(null);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [hasHover, expandedId]);

  // Clear any pending hover-intent timers on unmount so stale timeouts
  // never fire on unmounted state.
  useEffect(() => () => {
    if (enterTimer.current) clearTimeout(enterTimer.current);
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    enterTimer.current = null;
    leaveTimer.current = null;
  }, []);

  const clearTimers = useCallback(() => {
    if (enterTimer.current) {
      clearTimeout(enterTimer.current);
      enterTimer.current = null;
    }
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
  }, []);

  // Cursor entered an item (or it received keyboard focus). Schedule
  // expansion after the enter delay; cancel any pending collapse.
  const handleHoverStart = useCallback((id, item) => {
    // Non-permitted role: no hover-lift/expand affordance at all (§30).
    if (!interactiveFor(item)) return;
    clearTimers();
    if (expandedIdRef.current === id) return;
    if (reducedMotion) {
      setExpandedId(id);
      return;
    }
    enterTimer.current = setTimeout(() => {
      enterTimer.current = null;
      setExpandedId(id);
    }, ENTER_DELAY_MS);
  }, [clearTimers, reducedMotion, interactiveFor]);

  // Cursor left an item (or blur on a collapsed strip). If a pending
  // expansion hasn't fired yet, cancel it outright. Otherwise schedule
  // the grace-period collapse of whatever is currently expanded.
  const handleHoverEnd = useCallback(() => {
    if (enterTimer.current) {
      clearTimeout(enterTimer.current);
      enterTimer.current = null;
    }
    if (expandedIdRef.current == null) return;
    if (reducedMotion) {
      setExpandedId(null);
      return;
    }
    if (leaveTimer.current) return; // earlier leave already in flight
    leaveTimer.current = setTimeout(() => {
      leaveTimer.current = null;
      setExpandedId(null);
    }, LEAVE_GRACE_MS);
  }, [reducedMotion]);

  const handleClick = useCallback((item) => {
    // Non-permitted role: route straight through (no-op for gated roles) —
    // never expand a card the role can't open (§30).
    if (hasHover || !interactiveFor(item)) {
      onItemOpen(item);
      return;
    }
    // Touch device: first tap expands, second tap on the expanded card
    // navigates. Tapping a different collapsed strip shifts expansion.
    if (expandedIdRef.current === idOf(item)) {
      onItemOpen(item);
    } else {
      clearTimers();
      setExpandedId(idOf(item));
    }
  }, [hasHover, onItemOpen, clearTimers, interactiveFor, idOf]);

  if (!items || items.length === 0) {
    return <p style={emptyStyle}>{emptyMessage}</p>;
  }

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {items.map((item, index) => {
        const id = idOf(item);
        const isExpanded = expandedId === id;
        const itemInteractive = interactiveFor(item);
        const hoverBind = hasHover
          ? { onMouseEnter: () => handleHoverStart(id, item), onMouseLeave: handleHoverEnd }
          : {};
        // Focus on a collapsed strip mirrors hover; handlers are only live
        // on the collapsed element itself (passed via focusBind).
        const focusBind = {
          onFocus: () => handleHoverStart(id, item),
          onBlur: handleHoverEnd,
        };
        const wrapperStyle = {
          position: 'relative',
          zIndex: isExpanded ? 50 : Math.max(1, items.length - index),
          // Constant margin — no layout jump when the content swaps.
          marginTop: index === 0 ? 0 : '-6px',
          transition: reducedMotion ? 'none' : 'box-shadow 200ms ease-out',
        };
        return (
          <div key={id} style={wrapperStyle} {...hoverBind}>
            {isExpanded
              ? renderExpanded(item, {
                  onClick: () => handleClick(item),
                  interactive: itemInteractive,
                })
              : renderCollapsed(item, {
                  onClick: () => handleClick(item),
                  focusBind,
                  reducedMotion,
                  interactive: itemInteractive,
                })}
          </div>
        );
      })}
    </div>
  );
}

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);
  return reduced;
}
